/* eslint-disable @typescript-eslint/no-explicit-any */
import { Client, fetchExchange, subscriptionExchange } from '@urql/core';
import { AuthConfig, authExchange } from '@urql/exchange-auth';
import { SubscribePayload, createClient as createWSClient } from 'graphql-ws';
import { getSdk } from './generated/urql.limited-access.universal';
import { createSessionService } from './session';

export class UrqlGqlLimitedUniversal {
  private gqlClient: Client;
  private wsClient: ReturnType<typeof createWSClient>;

  private static userId = '';

  /**
   * This client is handed its own endpoints, so it keeps its own session reader
   * rather than the app-wide one — but the `whoami` handling itself lives in one
   * place now (PSTUDIO-4022). No auth exchange here: limited access is also used
   * unauthenticated, where an expired session is not something to sign out of.
   */
  private session: ReturnType<typeof createSessionService>;

  constructor(
    gqlEndpoint: string,
    wssEndpoint: string,
    kratosEndpoint: string
  ) {
    this.session = createSessionService({ kratosEndpoint });

    this.wsClient = createWSClient({
      url: wssEndpoint,
      connectionParams: () => {
        return {
          credentials: 'include',
          headers: {
            'x-hasura-user-id': UrqlGqlLimitedUniversal.userId,
          },
        };
      },
    });

    this.gqlClient = new Client({
      url: gqlEndpoint,
      requestPolicy: 'cache-and-network',
      exchanges: [
        authExchange(async (utils) => {
          UrqlGqlLimitedUniversal.userId =
            (await this.session.getIdentityId()) ?? '';

          return {
            addAuthToOperation(operation) {
              if (!UrqlGqlLimitedUniversal.userId) return operation;
              return utils.appendHeaders(operation, {
                'x-hasura-user-id': UrqlGqlLimitedUniversal.userId,
              });
            },
          } as AuthConfig;
        }),
        fetchExchange,
        subscriptionExchange({
          forwardSubscription: (operation) => {
            return {
              subscribe: (sink) => {
                const dispose = this.wsClient.subscribe(
                  operation as SubscribePayload,
                  sink
                );
                return {
                  unsubscribe: dispose,
                };
              },
            };
          },
        }),
      ],
      fetchOptions: () => {
        const headers: HeadersInit = {};
        if (UrqlGqlLimitedUniversal.userId) {
          headers['x-hasura-user-id'] = UrqlGqlLimitedUniversal.userId;
        }

        return {
          credentials: 'include',
          headers: headers,
        };
      },
    });
  }

  public getGqlClientSdk() {
    return getSdk(this.gqlClient);
  }
}

export * from './generated/urql.limited-access.universal';
